import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert } from '@mui/material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';

const IcrSchedulingSettings = () => {
    const [scheduleDays, setScheduleDays] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const settingsDocRef = useCallback(() => doc(db, 'settings', 'platformDefaults'), []);

    useEffect(() => {
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const docSnap = await getDoc(settingsDocRef());
                if (docSnap.exists()) {
                    setScheduleDays(docSnap.data().icrRunIntervalDays || '');
                }
            } catch (err) {
                console.error("Error fetching ICR settings:", err);
                setError('Failed to load schedule settings.');
                toast.error('Failed to load schedule settings.');
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [settingsDocRef]);

    const handleSave = async () => {
        const days = parseInt(scheduleDays, 10);
        if (isNaN(days) || days <= 0) {
            setError('Please enter a valid positive number of days.');
            return;
        }
        setError('');
        setIsSaving(true);
        const toastId = toast.loading("Saving settings...");
        try {
            await setDoc(settingsDocRef(), { icrRunIntervalDays: days }, { merge: true });
            toast.update(toastId, { render: "Schedule saved successfully!", type: "success", isLoading: false, autoClose: 4000 });
        } catch (err) {
            console.error("Error saving ICR settings:", err);
            toast.update(toastId, { render: `Failed to save settings: ${err.message}`, type: "error", isLoading: false, autoClose: 6000 });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" gutterBottom>
                Automated ICR Schedule
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Set how often the system should automatically regenerate the Initial Company Research (ICR) report for all active clients.
            </Typography>

            {loading ? (
                <CircularProgress />
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TextField
                        label="Run ICR Every (Days)"
                        type="number"
                        variant="outlined"
                        value={scheduleDays}
                        onChange={(e) => setScheduleDays(e.target.value)}
                        disabled={isSaving}
                        sx={{ maxWidth: 200 }}
                        InputProps={{ inputProps: { min: 1 } }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving || loading}
                    >
                        {isSaving ? <CircularProgress size={24} /> : 'Save Schedule'}
                    </Button>
                </Box>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Paper>
    );
};

export default IcrSchedulingSettings;